import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from "recharts";
import SSBReportShell from "./SSBReportShell";

//const AML_API = "http://localhost:9191/v1/ssb/aml";
const AML_API =
  process.env.REACT_APP_SSB_AML_API_BASE ||
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:9191/v1/ssb/aml"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app/v1/ssb/aml");

// ── Formatters ────────────────────────────────────────────────────────────────
const int0      = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const pct1      = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(1)}%`);
const safePct   = (num, den) => Number(den) > 0 ? (Number(num || 0) / Number(den)) * 100 : 0;
const shortProg = (s) => (s || "—").replace("SSB Bank FBO ", "").replace(" Customers", "");


// ── Macro-category colour palette (matches reference image) ───────────────────
const CATEGORY_COLORS = {
  "Structuring":          "#3B82F6",  // blue
  "High-Risk Activity":   "#F97316",  // orange
  "Velocity & Volume":    "#22C55E",  // green
  "Behavioral Anomalies": "#EF4444",  // red
  "Sanctions & KYC":      "#A855F7",  // purple
  "Other":                "#94A3B8",  // slate
};
const RULE_GRAIN = {
  "AML: Fintech Product Aggregate Deviation": "Product",
  "AML: Transaction Structuring": "Account",
  "AML: Burst of Inbound Activity": "Account",
  "AML: Microbursts Detection (Velocity)": "Account",
  "AML: Rapid Pass-Through Funds": "Account",
  "AML: Outflow to Common Beneficiary": "Account",
  "AML: Dormant Account Reactivation": "Account",
  "AML: High Value Transactions After Onboarding": "Account",
  "AML: High Value Outflow After Onboarding": "Account",
  "AML: High-Risk Jurisdiction": "Account",
  "AML: High-Risk Jurisdiction (Source)": "Account",
  "AML: Sanctions Hit": "Identity",
  "AML: PEP Exposure Hit": "Identity",
};

const ruleGrain = (ruleName) => RULE_GRAIN[ruleName] || "Account";

const CATEGORY_ORDER = [
  "Velocity & Volume", "Behavioral Anomalies", "Structuring",
  "High-Risk Activity", "Sanctions & KYC", "Other",
];

const RESULT_COLORS = { pass: "#22C55E", review: "#F59E0B", block: "#EF4444" };

const CARD_STYLE = {
  background: "#fff",
  border: "1px solid #E7ECF3",
  borderRadius: 18,
  boxShadow: "0 8px 24px rgba(16,24,40,0.06)",
};

const SECTION_GAP = 18;

// ── Pill ──────────────────────────────────────────────────────────────────────
const Pill = ({ text, tone = "info" }) => {
  const map = {
    info:    { bg: "#EFF8FF", fg: "#175CD3" },
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good:    { bg: "#ECFDF3", fg: "#027A48" },
    warn:    { bg: "#FFFAEB", fg: "#B54708" },
    bad:     { bg: "#FEF3F2", fg: "#B42318" },
  };
  const c = map[tone] || map.info;
  return (
    <span style={{
      padding: "4px 10px", borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 12, fontWeight: 900, border: "1px solid rgba(0,0,0,0.04)",
      letterSpacing: 0.2, whiteSpace: "nowrap",
    }}>{text}</span>
  );
};

const StatCard = ({ label, value, sub, tone }) => {
  const border = tone === "bad" ? "#FCA5A5" : tone === "warn" ? "#FCD34D" : "#E7ECF3";
  return (
    <div style={{
      background: "#fff", border: `1px solid ${border}`, borderRadius: 16,
      padding: 18, boxShadow: "0 10px 30px rgba(16,24,40,0.06)", minHeight: 108,
    }}>
      <div style={{ fontSize: 12, color: "#5E6B7A", fontWeight: 800, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 950, color: "#0B1F33", lineHeight: 1.1 }}>{value}</div>
      {sub ? <div style={{ marginTop: 6, fontSize: 12, color: "#7A8696" }}>{sub}</div> : null}
    </div>
  );
};

// ── Custom tooltips ───────────────────────────────────────────────────────────
const RuleTip = ({ active, payload, portfolioMode }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const grain = d.display_grain || ruleGrain(d.rule_name);

  if (portfolioMode) return (
    <div style={{
      background: "#fff", border: "1px solid #E7ECF3", borderRadius: 10,
      padding: "8px 12px", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
      maxWidth: 280,
    }}>
      <div style={{ fontSize: 11, color: "#4338CA", fontWeight: 800 }}>
        {grain}-level signal · Click ▶ to view breakdown by program
      </div>
    </div>
  );

  return (
    <div style={{
      background: "#fff", border: "1px solid #E7ECF3", borderRadius: 12,
      padding: "10px 14px", fontSize: 13, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
      maxWidth: 280,
    }}>
      <div style={{ fontWeight: 900, marginBottom: 4, color: "#0B1F33" }}>{d.rule_name}</div>
      <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 800, marginBottom: 4 }}>
        {grain}-level signal
      </div>
      <div style={{ color: CATEGORY_COLORS[d.macro_category] || "#94A3B8", fontWeight: 700, fontSize: 11, marginBottom: 6 }}>
        {d.macro_category}
      </div>
      <div style={{ color: "#374151", marginBottom: 6 }}>Account Hits: <b>{int0(d.hit_count)}</b></div>
      <div style={{ fontSize: 11, color: "#1D4ED8", fontWeight: 700 }}>Click rule name to view flagged accounts →</div>
    </div>
  );
};

// ── Y-axis tick for rule hit chart with static grain badge ───────────────────
const GRAIN_COLORS = { Product: "#7C3AED", Account: "#0369A1", Identity: "#B45309", Program: "#059669" };
const RuleAxisTick = ({ x, y, payload, selectedRule, onSelect, clickable = true, grainFn }) => {
  const isSelected = selectedRule === payload.value;
  const grain = grainFn ? grainFn(payload.value) : ruleGrain(payload.value);
  const grainColor = GRAIN_COLORS[grain] || "#6B7280";
  const badgeW = 62;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-(badgeW + 8)} y={0} dy={4}
        textAnchor="end"
        fontSize={12}
        fontWeight={isSelected ? 900 : 700}
        fill={isSelected ? "#1D4ED8" : "#1E293B"}
        style={clickable ? { cursor: "pointer", textDecoration: isSelected ? "underline" : "none" } : {}}
        onClick={clickable ? () => onSelect(payload.value) : undefined}
      >
        {payload.value}
      </text>
      <g transform={`translate(${-(badgeW)}, -9)`}>
        <rect width={badgeW - 4} height={18} rx={4} fill={grainColor} opacity={0.12} />
        <rect width={badgeW - 4} height={18} rx={4} fill="none" stroke={grainColor} strokeWidth={1} opacity={0.5} />
        <text x={(badgeW - 4) / 2} y={12} textAnchor="middle" fontSize={10} fontWeight={800} fill={grainColor}>
          {grain}
        </text>
      </g>
    </g>
  );
};

// ── Dropdown helper ───────────────────────────────────────────────────────────
const Select = ({ value, onChange, options, disabled, minWidth = 180, maxWidth }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    style={{
      padding: "7px 36px 7px 12px", borderRadius: 10, border: "1.5px solid #CBD5E1",
      fontSize: 13, fontWeight: 700, color: "#0B1F33", background: "#F8FAFC",
      cursor: "pointer", outline: "none", appearance: "none", minWidth,
      ...(maxWidth ? { maxWidth, width: maxWidth, overflow: "hidden", textOverflow: "ellipsis" } : {}),
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231D4ED8' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
    }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const PAGE_SIZE = 50;

const PaginationBar = ({ page, hasMore, onPrev, onNext, loading, count }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, gap: 8 }}>
    <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
      {count > 0 ? `Showing ${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + count}${hasMore ? "+" : ""}` : ""}
    </div>
    <div style={{ display: "flex", gap: 6 }}>
      <button
        onClick={onPrev}
        disabled={page === 0 || loading}
        style={{
          padding: "4px 12px", borderRadius: 7, border: "1px solid #E7ECF3",
          fontSize: 12, fontWeight: 700, cursor: page === 0 || loading ? "default" : "pointer",
          background: page === 0 || loading ? "#F8FAFC" : "#fff",
          color: page === 0 || loading ? "#CBD5E1" : "#374151",
        }}
      >← Prev</button>
      <span style={{ padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#374151" }}>
        Page {page + 1}
      </span>
      <button
        onClick={onNext}
        disabled={!hasMore || loading}
        style={{
          padding: "4px 12px", borderRadius: 7, border: "1px solid #E7ECF3",
          fontSize: 12, fontWeight: 700, cursor: !hasMore || loading ? "default" : "pointer",
          background: !hasMore || loading ? "#F8FAFC" : "#fff",
          color: !hasMore || loading ? "#CBD5E1" : "#374151",
        }}
      >Next →</button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function SSBAmlExecutionReport() {

  // ── Filter state ──────────────────────────────────────────────────────────
  const [periodType, setPeriodType]         = useState("weekly");
  const [filters, setFilters]               = useState({ dates: [], weeks: [], months: [], quarters: [] });
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [customFrom, setCustomFrom]         = useState("");
  const [customTo, setCustomTo]             = useState("");
  const [selectedProgram] = useState("All");
  const [programList, setProgramList]       = useState([]);   // all programs from /programs

  // ── Data state ────────────────────────────────────────────────────────────
  const [executions, setExecutions]     = useState([]);
  const [programs, setPrograms]         = useState([]);
  const [accounts, setAccounts]         = useState([]);
  const [ruleHits, setRuleHits]         = useState([]);

  //ACB 05/13 added
  const [programExposure, setProgramExposure] = useState([]);
  const [loadingExposure, setLoadingExposure] = useState(false);

  // ── UI filters ────────────────────────────────────────────────────────────
  const [resultFilter]                    = useState("ALL");

  const [selectedRule, setSelectedRule]         = useState(null);
  const [ruleAccounts, setRuleAccounts]         = useState([]);
  const [loadingRuleAccts, setLoadingRuleAccts] = useState(false);
  const [ruleProgramSummary, setRuleProgramSummary]         = useState([]);
  const [loadingRuleProgSummary, setLoadingRuleProgSummary] = useState(false);
  const [ruleProductBreakdown, setRuleProductBreakdown]     = useState([]); // [{program_name, products:[{product_name,account_count}]}]
  const [loadingRuleProduct, setLoadingRuleProduct]         = useState(false);
  const [ruleBarProductSummary, setRuleBarProductSummary]   = useState([]); // flat [{product_name, account_count}] for ▶ expand panel on Rule 8
  const [selectedProduct, setSelectedProduct]               = useState(null); // {program_name, product_name}
  const [ruleSort, setRuleSort]                             = useState({ col: null, dir: "asc" });

  // ── Account transaction drill-down ────────────────────────────────────────
  const [expandedAcct, setExpandedAcct]       = useState(null); // { account, ref_date, rule_name, date_from, date_to }
  const [acctTxns, setAcctTxns]               = useState([]);
  const [acctTxnFallback, setAcctTxnFallback] = useState(null);
  const [loadingAcctTxns, setLoadingAcctTxns] = useState(false);

  // ── Loading ───────────────────────────────────────────────────────────────
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingExec,    setLoadingExec]    = useState(false);
  const [loadingProg,    setLoadingProg]    = useState(false);
  const [loadingRules,   setLoadingRules]   = useState(false);
  const [error, setError]                   = useState(null);

  // ── Tab + Program Insights state ──────────────────────────────────────────
  const [activeTab, setActiveTab]                       = useState("portfolio");
  const [ruleBarExpanded, setRuleBarExpanded]           = useState(null); // rule_name with ▶ open
  const [insightsProgram, setInsightsProgram]           = useState(null);
  const [expandedPrograms, setExpandedPrograms]         = useState(new Set()); // expanded tree nodes
  const [leftWidth, setLeftWidth]                       = useState(320); // resizable left panel px
  const [insightsProduct, setInsightsProduct]           = useState(null); // selected product name
  const [insightsAccount, setInsightsAccount]           = useState(null); // selected account number
  const [insightsRuleHits, setInsightsRuleHits]         = useState([]);
  const [insightsSelectedRule, setInsightsSelectedRule] = useState(null);
  const [insightsAccounts, setInsightsAccounts]         = useState([]);
  const [insightsTxnRow, setInsightsTxnRow]             = useState(null); // {account, ref_date, rule_name}
  const [insightsTxnData, setInsightsTxnData]           = useState([]);
  const [insightsTxnFallback, setInsightsTxnFallback]   = useState(null);
  const [loadingInsightsRules, setLoadingInsightsRules] = useState(false);
  const [loadingInsightsAccts, setLoadingInsightsAccts] = useState(false);
  const [loadingInsightsTxn, setLoadingInsightsTxn]     = useState(false);
  const [insightsSort, setInsightsSort]                 = useState({ col: "ref_date", dir: "desc" });

  // ── Pagination for rule-accounts tables ───────────────────────────────────
  const [ruleAcctPage, setRuleAcctPage]           = useState(0);
  const [ruleAcctHasMore, setRuleAcctHasMore]     = useState(false);
  const [insightsAcctPage, setInsightsAcctPage]   = useState(0);
  const [insightsAcctHasMore, setInsightsAcctHasMore] = useState(false);

  // ── Step 1: load filters + program list on mount ─────────────────────────
  useEffect(() => {
    let mounted = true;
    setLoadingFilters(true);
    (async () => {
      try {
        const [filtersData, programsData] = await Promise.all([
          fetch(`${AML_API}/filters`).then((r) => r.json()),
          fetch(`${AML_API}/programs`).then((r) => r.json()),
        ]);
        if (!mounted) return;
        setFilters({
          dates:    filtersData.dates    || [],
          weeks:    filtersData.weeks    || [],
          months:   filtersData.months   || [],
          quarters: filtersData.quarters || [],
        });
        setProgramList(programsData.programs || []);
        if (filtersData.weeks?.length) setSelectedPeriod(filtersData.weeks[0].label);
        else if (filtersData.dates?.length) setSelectedPeriod(filtersData.dates[0]);
      } catch (e) {
        if (mounted) setError(`Cannot reach AML API at ${AML_API}. Please check backend route/CORS.`);
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Derived: date range from selected period ──────────────────────────────
  const { dateFrom, dateTo } = useMemo(() => {
    if (periodType === "custom") {
      if (!customFrom || !customTo) return { dateFrom: null, dateTo: null };
      return { dateFrom: customFrom, dateTo: customTo };
    }
    if (!selectedPeriod) return { dateFrom: null, dateTo: null };
    if (periodType === "daily") return { dateFrom: selectedPeriod, dateTo: selectedPeriod };

    // Monthly: force calendar start/end regardless of which days have transactions
    if (periodType === "monthly") {
      const [year, month] = selectedPeriod.split("-").map(Number);
      const start = `${selectedPeriod}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${selectedPeriod}-${String(lastDay).padStart(2, "0")}`;
      return { dateFrom: start, dateTo: end };
    }

    // Quarterly: force calendar start/end of the quarter
    if (periodType === "quarterly") {
      const [yearStr, qStr] = selectedPeriod.split("-Q");
      const year = Number(yearStr);
      const q = Number(qStr);
      const startMonth = (q - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      const start = `${yearStr}-${String(startMonth).padStart(2, "0")}-01`;
      const lastDay = new Date(year, endMonth, 0).getDate();
      const end = `${yearStr}-${String(endMonth).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { dateFrom: start, dateTo: end };
    }

    // Weekly: use actual dates from bucket (week may not align to Mon/Sun)
    const bucket = filters.weeks.find((b) => b.label === selectedPeriod);
    if (!bucket?.dates?.length) return { dateFrom: null, dateTo: null };
    const sorted = [...bucket.dates].sort();
    return { dateFrom: sorted[0], dateTo: sorted[sorted.length - 1] };
  }, [periodType, selectedPeriod, filters, customFrom, customTo]);

  // ── Step 2: load executions whenever date range or program changes ────────
  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let mounted = true;
    setLoadingExec(true);
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        if (selectedProgram !== "All") qs.set("program_name", selectedProgram);
        const data = await fetch(`${AML_API}/executions?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setExecutions(Array.isArray(data.executions) ? data.executions : []);
      } catch (e) {
        if (mounted) setExecutions([]);
      } finally {
        if (mounted) setLoadingExec(false);
      }
    })();
    return () => { mounted = false; };
  }, [dateFrom, dateTo, selectedProgram]);

  // ── Step 3: load program breakdown for the full date range ──────────────
  // (program breakdown and flagged accounts now use date range, not execution_id)
  useEffect(() => {
    if (!dateFrom || !dateTo) { setPrograms([]); return; }
    let mounted = true;
    setLoadingProg(true);
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        const data = await fetch(`${AML_API}/programs-summary?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setPrograms(Array.isArray(data.programs) ? data.programs : []);
      } catch (e) {
        if (mounted) setPrograms([]);
      } finally {
        if (mounted) setLoadingProg(false);
      }
    })();
    return () => { mounted = false; };
  }, [dateFrom, dateTo]);

  // ── Load top AML exposure programs for executive view ───────────────────────
useEffect(() => {
  if (!dateFrom || !dateTo) {
    setProgramExposure([]);
    return;
  }

  let mounted = true;
  setLoadingExposure(true);

  (async () => {
    try {
      const qs = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
        limit: "10",
      });

      const data = await fetch(`${AML_API}/program-exposure?${qs}`).then((r) => r.json());
      if (!mounted) return;

      setProgramExposure(Array.isArray(data.programs) ? data.programs : []);
    } catch (e) {
      if (mounted) setProgramExposure([]);
    } finally {
      if (mounted) setLoadingExposure(false);
    }
  })();

  return () => {
    mounted = false;
  };
}, [dateFrom, dateTo]);

  // ── Step 4: load rule hits for the full date range ────────────────────────
  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let mounted = true;
    setLoadingRules(true);
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        if (selectedProgram !== "All") qs.set("program_name", selectedProgram);
        const data = await fetch(`${AML_API}/rule-hits?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setRuleHits(Array.isArray(data.rule_hits) ? data.rule_hits : []);
      } catch (e) {
        if (mounted) setRuleHits([]);
      } finally {
        if (mounted) setLoadingRules(false);
      }
    })();
    return () => { mounted = false; };
  }, [dateFrom, dateTo, selectedProgram]);

  // ── Step 5: load flagged accounts for the full date range ────────────────
  useEffect(() => {
    if (!dateFrom || !dateTo) { setAccounts([]); return; }
    let mounted = true;
    const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, limit: "10000" });
    if (selectedProgram !== "All") qs.set("program_name", selectedProgram);
    if (resultFilter !== "ALL") qs.set("result", resultFilter);
    (async () => {
      try {
        const data = await fetch(`${AML_API}/accounts?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
      } catch (e) {
        if (mounted) setAccounts([]);
      }
    })();
    return () => { mounted = false; };
  }, [dateFrom, dateTo, selectedProgram, resultFilter]);

  // Match both old (Program) and new (Product) name during backfill transition
  const IS_RULE8 = ruleGrain(selectedRule) === "Product";

  // ── Step 6a: load per-program summary for ▶ expanded rule (skip for Rule 8 — uses product breakdown instead) ───────────────
  useEffect(() => {
    if (!ruleBarExpanded || !dateFrom || !dateTo || ruleGrain(ruleBarExpanded) === "Product") { setRuleProgramSummary([]); return; }
    let mounted = true;
    setLoadingRuleProgSummary(true);
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, rule_name: ruleBarExpanded });
        const data = await fetch(`${AML_API}/rule-program-summary?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setRuleProgramSummary(Array.isArray(data.programs) ? data.programs : []);
      } catch (e) {
        if (mounted) setRuleProgramSummary([]);
      } finally {
        if (mounted) setLoadingRuleProgSummary(false);
      }
    })();
    return () => { mounted = false; };
  }, [ruleBarExpanded, dateFrom, dateTo]);

  // ── Step 6a-ii: for Rule 8 ▶ expand panel, load flat product summary ────
  useEffect(() => {
    if (!ruleBarExpanded || ruleGrain(ruleBarExpanded) !== "Product" || !dateFrom || !dateTo) {
      setRuleBarProductSummary([]);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, rule_name: ruleBarExpanded });
        const data = await fetch(`${AML_API}/rule-product-breakdown?${qs}`).then((r) => r.json());
        if (!mounted) return;
        const programs = Array.isArray(data.programs) ? data.programs : [];
        // Flatten all products across programs, summing account_count
        const productMap = {};
        programs.forEach((prog) => {
          (prog.products || []).forEach((prod) => {
            const key = prod.product_name || "Unknown Product";
            productMap[key] = (productMap[key] || 0) + prod.account_count;
          });
        });
        const flat = Object.entries(productMap)
          .map(([product_name, account_count]) => ({ product_name, account_count }))
          .sort((a, b) => b.account_count - a.account_count);
        setRuleBarProductSummary(flat);
      } catch (e) {
        if (mounted) setRuleBarProductSummary([]);
      }
    })();
    return () => { mounted = false; };
  }, [ruleBarExpanded, dateFrom, dateTo]);

  // ── Step 6b: for Rule 8, load program→product breakdown ──────────────────
  useEffect(() => {
    if (!IS_RULE8 || !dateFrom || !dateTo) { setRuleProductBreakdown([]); setSelectedProduct(null); return; }
    let mounted = true;
    setLoadingRuleProduct(true);
    setSelectedProduct(null);
    setRuleAccounts([]);
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, rule_name: selectedRule });
        const data = await fetch(`${AML_API}/rule-product-breakdown?${qs}`).then((r) => r.json());
        if (!mounted) return;
        const programs = Array.isArray(data.programs) ? data.programs : [];
        setRuleProductBreakdown(programs);
        // If a specific program is already selected, auto-select it so we skip the program cards
        if (selectedProgram && selectedProgram !== "All") {
          const match = programs.find((p) => p.program_name === selectedProgram);
          if (match) setSelectedProduct({ program_name: selectedProgram });
        }
      } catch (e) {
        if (mounted) setRuleProductBreakdown([]);
      } finally {
        if (mounted) setLoadingRuleProduct(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedRule, dateFrom, dateTo, selectedProgram]);

  // Reset rule-accounts page when any filter changes
  useEffect(() => {
    setRuleAcctPage(0);
    setExpandedAcct(null);
  }, [selectedRule, dateFrom, dateTo, selectedProgram, selectedProduct]);

  // ── Step 6: load accounts for clicked rule ────────────────────────────────
  // For Rule 8: only fetch when a product is selected; pass product_name param.
  // For all other rules: fetch immediately on rule selection.
  useEffect(() => {
    if (!selectedRule || !dateFrom || !dateTo) { setRuleAccounts([]); setRuleAcctHasMore(false); return; }
    if (IS_RULE8 && !selectedProduct?.product_name) { setRuleAccounts([]); setRuleAcctHasMore(false); return; }
    let mounted = true;
    setLoadingRuleAccts(true);
    (async () => {
      try {
        const qs = new URLSearchParams({
          date_from: dateFrom, date_to: dateTo, rule_name: selectedRule,
          limit: String(PAGE_SIZE), offset: String(ruleAcctPage * PAGE_SIZE),
        });
        if (IS_RULE8 && selectedProduct?.product_name) {
          qs.set("program_name", selectedProduct.program_name);
          qs.set("product_name", selectedProduct.product_name);
        } else if (selectedProgram !== "All") {
          qs.set("program_name", selectedProgram);
        }
        const data = await fetch(`${AML_API}/rule-accounts?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setRuleAccounts(Array.isArray(data.accounts) ? data.accounts : []);
        setRuleAcctHasMore(data.has_more ?? false);
      } catch (e) {
        if (mounted) { setRuleAccounts([]); setRuleAcctHasMore(false); }
      } finally {
        if (mounted) setLoadingRuleAccts(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedRule, dateFrom, dateTo, selectedProgram, selectedProduct, ruleAcctPage]);

  // ── Step 7: fetch transactions for expanded account row ──────────────────
  useEffect(() => {
    if (!expandedAcct) { setAcctTxns([]); setAcctTxnFallback(null); return; }
    let mounted = true;
    setLoadingAcctTxns(true);
    (async () => {
      try {
        const qs = new URLSearchParams({
          account:  expandedAcct.account,
          ref_date: expandedAcct.ref_date,
        });
        if (expandedAcct.rule_name) qs.set("rule_name", expandedAcct.rule_name);

        // Use evidence-backed endpoint when a rule is known; fall back to heuristic endpoint
        const endpoint = expandedAcct.rule_name
          ? `${AML_API}/account-triggering-txns`
          : `${AML_API}/account-transactions`;

        if (!expandedAcct.rule_name) {
          if (expandedAcct.date_from) qs.set("date_from", expandedAcct.date_from);
          if (expandedAcct.date_to)   qs.set("date_to",   expandedAcct.date_to);
        }

        const data = await fetch(`${endpoint}?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setAcctTxns(Array.isArray(data.transactions) ? data.transactions : []);
        setAcctTxnFallback(data.fallback ?? null);
      } catch (e) {
        if (mounted) { setAcctTxns([]); setAcctTxnFallback(null); }
      } finally {
        if (mounted) setLoadingAcctTxns(false);
      }
    })();
    return () => { mounted = false; };
  }, [expandedAcct]);

  // ── Step A: rule hits for Program Insights (scoped to insightsProgram) ─────
  useEffect(() => {
    if (!dateFrom || !dateTo || !insightsProgram) { setInsightsRuleHits([]); return; }
    let mounted = true;
    setLoadingInsightsRules(true);
    setInsightsSelectedRule(null);
    setInsightsAccounts([]);
    (async () => {
      try {
        const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, program_name: insightsProgram });
        const data = await fetch(`${AML_API}/rule-hits?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setInsightsRuleHits(Array.isArray(data.rule_hits) ? data.rule_hits : []);
      } catch (e) {
        if (mounted) setInsightsRuleHits([]);
      } finally {
        if (mounted) setLoadingInsightsRules(false);
      }
    })();
    return () => { mounted = false; };
  }, [dateFrom, dateTo, insightsProgram]);

  // Reset insights-accounts page when any filter changes
  useEffect(() => {
    setInsightsAcctPage(0);
    setInsightsTxnRow(null);
  }, [insightsSelectedRule, dateFrom, dateTo, insightsProgram, insightsProduct]);

  // ── Step B: accounts for clicked rule in Program Insights ────────────────
  useEffect(() => {
    if (!insightsSelectedRule || !dateFrom || !dateTo || !insightsProgram) {
      setInsightsAccounts([]); setInsightsAcctHasMore(false); return;
    }
    let mounted = true;
    setLoadingInsightsAccts(true);
    (async () => {
      try {
        const qs = new URLSearchParams({
          date_from: dateFrom, date_to: dateTo,
          rule_name: insightsSelectedRule, program_name: insightsProgram,
          limit: String(PAGE_SIZE), offset: String(insightsAcctPage * PAGE_SIZE),
        });
        if (insightsProduct) qs.set("product_name", insightsProduct);
        const data = await fetch(`${AML_API}/rule-accounts?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setInsightsAccounts(Array.isArray(data.accounts) ? data.accounts : []);
        setInsightsAcctHasMore(data.has_more ?? false);
      } catch (e) {
        if (mounted) { setInsightsAccounts([]); setInsightsAcctHasMore(false); }
      } finally {
        if (mounted) setLoadingInsightsAccts(false);
      }
    })();
    return () => { mounted = false; };
  }, [insightsSelectedRule, dateFrom, dateTo, insightsProgram, insightsProduct, insightsAcctPage]);

  // ── Step C: transactions for expanded row in Program Insights ─────────────
  useEffect(() => {
    if (!insightsTxnRow) { setInsightsTxnData([]); setInsightsTxnFallback(null); return; }
    let mounted = true;
    setLoadingInsightsTxn(true);
    (async () => {
      try {
        const qs = new URLSearchParams({ account: insightsTxnRow.account, ref_date: insightsTxnRow.ref_date });
        if (insightsTxnRow.rule_name) qs.set("rule_name", insightsTxnRow.rule_name);
        if (dateFrom) qs.set("date_from", dateFrom);
        if (dateTo)   qs.set("date_to", dateTo);
        const data = await fetch(`${AML_API}/account-transactions?${qs}`).then((r) => r.json());
        if (!mounted) return;
        setInsightsTxnData(Array.isArray(data.transactions) ? data.transactions : []);
        setInsightsTxnFallback(data.fallback ?? null);
      } catch (e) {
        if (mounted) { setInsightsTxnData([]); setInsightsTxnFallback(null); }
      } finally {
        if (mounted) setLoadingInsightsTxn(false);
      }
    })();
    return () => { mounted = false; };
  }, [insightsTxnRow]);

  // ── Aggregated KPIs across all executions in period ───────────────────────
  const kpi = useMemo(() => {
    if (!executions.length) return null;
    const total   = executions.reduce((s, e) => s + (e.total_accounts || 0), 0);
    const pass    = executions.reduce((s, e) => s + (e.pass_count    || 0), 0);
    const review  = executions.reduce((s, e) => s + (e.review_count  || 0), 0);
    const block   = executions.reduce((s, e) => s + (e.block_count   || 0), 0);
    const flagPct = safePct(review + block, total);
    return { total, pass, review, block, flagPct };
  }, [executions]);

  const riskTone = useMemo(() => {
    if (!kpi) return "neutral";
    return kpi.flagPct >= 25 ? "bad" : kpi.flagPct >= 10 ? "warn" : "good";
  }, [kpi]);

  // ── Chart: rule hits ──────────────────────────────────────────────────────
  const ruleChartData = useMemo(() =>
    [...ruleHits]
      .sort((a, b) => b.hit_count - a.hit_count)
      .map((r) => {
        const g = ruleGrain(r.rule_name);
        let display_count;
        if (g === "Product") display_count = r.distinct_products ?? r.hit_count;
        else if (g === "Program") display_count = r.distinct_programs ?? r.hit_count;
        else if (g === "Identity") display_count = r.distinct_accounts ?? r.hit_count;
        else display_count = r.hit_count;
        return { ...r, display_count, display_grain: g };
      }),
    [ruleHits],
  );

  // ── Program → product tree (from flagged accounts) ────────────────────────
  const programProductTree = useMemo(() => {
    // Build { program_name → { product_name → { count, accounts: [{account, result, ref_date}] } } }
    const tree = {};
    accounts.forEach((a) => {
      const prog = a.program_name || "Unknown";
      const prod = a.product_name || null;
      if (!tree[prog]) tree[prog] = {};
      if (prod) {
        if (!tree[prog][prod]) tree[prog][prod] = { count: 0, accounts: [], seen: new Set() };
        const key = `${a.account}__${a.ref_date}`;
        if (!tree[prog][prod].seen.has(key)) {
          tree[prog][prod].seen.add(key);
          tree[prog][prod].count++;
          tree[prog][prod].accounts.push({ account: a.account, result: a.result, ref_date: a.ref_date });
        }
      }
    });
    // Strip Set for rendering
    return Object.fromEntries(
      Object.entries(tree).map(([prog, prods]) => [
        prog,
        Object.fromEntries(Object.entries(prods).map(([prod, val]) => [prod, { count: val.count, accounts: val.accounts }])),
      ])
    );
  }, [accounts]);

  // ── Rule hits for selected product (computed from accounts) ───────────────
  const insightsProductRuleHits = useMemo(() => {
    if (!insightsProduct || !insightsProgram) return null;
    // Build a macro_category lookup from insightsRuleHits
    const catLookup = {};
    insightsRuleHits.forEach((r) => { catLookup[r.rule_name] = r.macro_category; });
    const ruleMap = {};
    accounts.forEach((a) => {
      if (a.program_name !== insightsProgram) return;
      if (a.product_name !== insightsProduct) return;
      (a.fired_rules || "").split(", ").filter(Boolean).forEach((rule) => {
        if (!ruleMap[rule]) ruleMap[rule] = new Set();
        ruleMap[rule].add(a.account);
      });
    });
    return Object.entries(ruleMap)
      .map(([rule_name, accts]) => ({
        rule_name,
        hit_count: accts.size,
        macro_category: catLookup[rule_name] || "Other",
      }))
      .sort((a, b) => b.hit_count - a.hit_count);
  }, [insightsProduct, insightsProgram, accounts, insightsRuleHits]);

  // ── Rule hits for selected account (computed from accounts fired_rules) ────
  const insightsAccountRuleHits = useMemo(() => {
    if (!insightsAccount || !insightsProgram) return null;
    const catLookup = {};
    insightsRuleHits.forEach((r) => { catLookup[r.rule_name] = r.macro_category; });
    const ruleMap = {};
    accounts.forEach((a) => {
      if (a.account !== insightsAccount) return;
      (a.fired_rules || "").split(", ").filter(Boolean).forEach((rule) => {
        ruleMap[rule] = (ruleMap[rule] || 0) + 1;
      });
    });
    return Object.entries(ruleMap)
      .map(([rule_name, hit_count]) => ({
        rule_name,
        hit_count,
        macro_category: catLookup[rule_name] || "Other",
      }))
      .sort((a, b) => b.hit_count - a.hit_count);
  }, [insightsAccount, insightsProgram, accounts, insightsRuleHits]);

  // ── Sorted rule accounts ──────────────────────────────────────────────────
  const sortedRuleAccounts = useMemo(() => {
    if (!ruleSort.col) return ruleAccounts;
    return [...ruleAccounts].sort((a, b) => {
      const va = (ruleSort.col === "program" ? a.program_name : a.ref_date) || "";
      const vb = (ruleSort.col === "program" ? b.program_name : b.ref_date) || "";
      return ruleSort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [ruleAccounts, ruleSort]);

  const toggleRuleSort = (col) =>
    setRuleSort((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));

  // ── Sorted + filtered insights accounts ──────────────────────────────────
  const sortedInsightsAccounts = useMemo(() => {
    let rows = insightsAccounts;
    // Filter to selected account if one is chosen in the tree
    if (insightsAccount) rows = rows.filter((a) => a.account === insightsAccount);
    return [...rows].sort((a, b) => {
      let va, vb;
      if (insightsSort.col === "product")  { va = a.product_name || ""; vb = b.product_name || ""; }
      else if (insightsSort.col === "status") { va = a.result || ""; vb = b.result || ""; }
      else { va = a.ref_date || ""; vb = b.ref_date || ""; }
      return insightsSort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [insightsAccounts, insightsSort, insightsAccount]);

  const toggleInsightsSort = (col) =>
    setInsightsSort((prev) => ({ col, dir: prev.col === col && prev.dir === "asc" ? "desc" : "asc" }));

  // ── Period selector options ───────────────────────────────────────────────
  const periodOptions = useMemo(() => {
    if (periodType === "daily")     return filters.dates.map((d) => ({ value: d, label: d }));
    if (periodType === "weekly")    return filters.weeks.map((b) => ({ value: b.label, label: `Week of ${b.label}` }));
    if (periodType === "monthly")   return filters.months.map((b) => ({ value: b.label, label: b.label }));
    return filters.quarters.map((b) => ({ value: b.label, label: b.label }));
  }, [periodType, filters]);

  const allProgramNames = useMemo(() =>
    ["All", ...programList],
    [programList],
  );


  const periodLabel = useMemo(() => {
    if (periodType === "custom") {
      if (!customFrom || !customTo) return "—";
      return `${customFrom} → ${customTo}`;
    }
    if (!selectedPeriod) return "—";
    if (periodType === "daily") return selectedPeriod;
    if (periodType === "monthly" || periodType === "quarterly") return selectedPeriod;
    return `${selectedPeriod} (${dateFrom} → ${dateTo})`;
  }, [periodType, selectedPeriod, dateFrom, dateTo, customFrom, customTo]);

  const programDistinctAccounts = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      if (!a.program_name || !a.account) return;
      if (!map[a.program_name]) map[a.program_name] = new Set();
      map[a.program_name].add(a.account);
    });
    return Object.fromEntries(Object.entries(map).map(([k, s]) => [k, s.size]));
  }, [accounts]);

  const executiveInsight = useMemo(() => {
    const flaggedAccounts = insightsAccount
      ? new Set(accounts.filter(a => a.account === insightsAccount).map(a => a.account).filter(Boolean)).size
      : insightsProduct && insightsProgram
      ? new Set(accounts.filter(a => a.program_name === insightsProgram && a.product_name === insightsProduct).map(a => a.account).filter(Boolean)).size
      : insightsProgram
      ? new Set(accounts.filter(a => a.program_name === insightsProgram).map(a => a.account).filter(Boolean)).size
      : new Set(accounts.map(a => a.account).filter(Boolean)).size;
    const topRule = ruleChartData[0]?.rule_name || "—";
    const highestRiskProgram = [...programs]
      .map((p) => ({
        ...p,
        flags: programDistinctAccounts[p.program_name] ?? (Number(p.review || 0) + Number(p.block || 0)),
        flagPct: safePct(programDistinctAccounts[p.program_name] ?? (Number(p.review || 0) + Number(p.block || 0)), p.total),
      }))
      .sort((a, b) => b.flags - a.flags || b.flagPct - a.flagPct)[0];

    const scope = insightsAccount
      ? `Account ${insightsAccount}`
      : insightsProduct
      ? insightsProduct
      : insightsProgram
      ? shortProg(insightsProgram)
      : "All SSB programs";

    return {
      scope,
      flaggedAccounts,
      topRule,
      highestRiskProgram: highestRiskProgram?.program_name ? shortProg(highestRiskProgram.program_name) : "—",
      highestRiskFlags: highestRiskProgram?.flags || 0,
    };
  }, [accounts, ruleChartData, programs, programDistinctAccounts, insightsAccount, insightsProduct, insightsProgram]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (error) return <div style={{ padding: 32, color: "#B42318", fontWeight: 700 }}>⚠ {error}</div>;

  return (
    <SSBReportShell
      title="SSB Risk Console"
      subtitle="Rule hit frequency, program decisions, and flagged accounts across all SSB fintech programs"
      leftMeta={[]}
      rightMeta={["AML Engine", "SSB Bank", "Live"]}
      footerLeft=""
    >

      {/* ── Tab Bar ───────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 4, background: "#F1F5FF", borderRadius: 12, padding: 4, marginBottom: 22, alignSelf: "flex-start" }}>
        {[["portfolio", "Portfolio Overview"], ["insights", "Program Insights"]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              padding: "8px 24px", borderRadius: 10, border: "none", cursor: "pointer",
              fontSize: 14, fontWeight: 700, transition: "all 0.15s",
              background: activeTab === id ? "#1D4ED8" : "transparent",
              color: activeTab === id ? "#fff" : "#1D4ED8",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Filters (always visible) ────────────────────────────────────────── */}
      <div style={{
        background: "#fff", border: "1px solid #E7ECF3", borderRadius: 16,
        padding: "14px 18px", marginBottom: SECTION_GAP,
        boxShadow: "0 4px 12px rgba(16,24,40,0.05)",
        display: "flex", alignItems: "center", gap: 10,
      }}>

        <span style={{ fontSize: 12, fontWeight: 800, color: "#5E6B7A", whiteSpace: "nowrap" }}>Period</span>
        {/* Period type tabs + period value picker — all on one line */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
          <div style={{ display: "flex", gap: 4, background: "#F1F5FF", borderRadius: 10, padding: 4 }}>
            {["daily", "weekly", "monthly", "quarterly", "custom"].map((pt) => (
              <button
                key={pt}
                onClick={() => {
                  setPeriodType(pt);
                  if (pt !== "custom") {
                    const opts = pt === "daily"     ? filters.dates
                               : pt === "weekly"    ? filters.weeks.map((b) => b.label)
                               : pt === "monthly"   ? filters.months.map((b) => b.label)
                               :                      filters.quarters.map((b) => b.label);
                    setSelectedPeriod(opts[0] || null);
                  }
                }}
                style={{
                  padding: "5px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 700, transition: "all 0.15s",
                  background: pt === periodType ? "#1D4ED8" : "transparent",
                  color: pt === periodType ? "#fff" : "#1D4ED8",
                }}
              >
                {pt.charAt(0).toUpperCase() + pt.slice(1)}
              </button>
            ))}
          </div>

          {/* Daily → native date picker; custom → from/to pickers; others → dropdown */}
          {periodType === "daily" ? (
            <input
              type="date"
              value={selectedPeriod || ""}
              min={filters.dates.length ? [...filters.dates].sort()[0] : undefined}
              max={filters.dates.length ? filters.dates[0] : undefined}
              onChange={(e) => e.target.value && setSelectedPeriod(e.target.value)}
              style={{
                padding: "7px 12px", borderRadius: 10, border: "1.5px solid #CBD5E1",
                fontSize: 13, fontWeight: 700, color: "#0B1F33", background: "#F8FAFC",
                cursor: "pointer", outline: "none",
              }}
            />
          ) : periodType === "custom" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{
                  padding: "7px 12px", borderRadius: 10, border: "1.5px solid #CBD5E1",
                  fontSize: 13, fontWeight: 700, color: "#0B1F33", background: "#F8FAFC",
                  cursor: "pointer", outline: "none",
                }}
              />
              <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 700 }}>→</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{
                  padding: "7px 12px", borderRadius: 10, border: "1.5px solid #CBD5E1",
                  fontSize: 13, fontWeight: 700, color: "#0B1F33", background: "#F8FAFC",
                  cursor: "pointer", outline: "none",
                }}
              />
            </div>
          ) : (
            <Select
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              options={periodOptions}
              disabled={loadingFilters || periodOptions.length === 0}
              minWidth={140}
              maxWidth={160}
            />
          )}

          {loadingExec && <span style={{ fontSize: 12, color: "#6B7280" }}>Loading…</span>}
          {!loadingExec && kpi && activeTab === "portfolio" && (
            <span style={{ fontSize: 12, color: "#6B7280" }}>
              {int0(kpi.total)} accounts · {executions.length} day{executions.length !== 1 ? "s" : ""} of execution
            </span>
          )}
          {activeTab === "insights" && (() => {
            const count = new Set(accounts.map(a => a.account).filter(Boolean)).size;
            return <span style={{ fontSize: 12, color: "#6B7280" }}>{int0(count)} flagged account{count !== 1 ? "s" : ""} across all programs</span>;
          })()}
        </div>
      </div>

      {/* ══════════════════════ PORTFOLIO OVERVIEW TAB ══════════════════════ */}
      {activeTab === "portfolio" && (<>

      {/* ── KPI Row ────────────────────────────────────────────────────────── */}
      {/* ── Executive Top Risk Programs View ───────────────────────────────────── */}
<div style={{
  background: "#fff",
  border: "1px solid #E7ECF3",
  borderRadius: 16,
  padding: "22px 26px",
  marginBottom: SECTION_GAP,
  boxShadow: "0 4px 12px rgba(16,24,40,0.05)",
}}>
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
    <div>
      <div style={{ fontSize: 16, fontWeight: 950, color: "#0B1F33" }}>
        Top AML Exposure Programs
      </div>
      <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
      Ranked by AML Exposure Score · severity, concentration, and operational impact · {periodLabel}
      </div>
    </div>
    <Pill text="Executive View" tone="neutral" />
  </div>

  {loadingExposure ? (
    <div style={{ color: "#6B7280", fontSize: 13 }}>Loading program exposure…</div>
  ) : programExposure.length === 0 ? (
    <div style={{ color: "#6B7280", fontSize: 13 }}>No program exposure data for this period.</div>
  ) : (
    <ResponsiveContainer width="100%" height={Math.max(260, programExposure.length * 44)}>
      <BarChart
        data={programExposure.map((p) => ({
          ...p,
          short_name: shortProg(p.program_name),
          exposure_score: Number(p.exposure_score || 0),
        }))}
        layout="vertical"
        margin={{ top: 0, right: 180, left: 170, bottom: 8 }}
        barCategoryGap="28%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#F0F4F8" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={{ stroke: "#E7ECF3" }} tickLine={false} />
        <YAxis
          type="category"
          dataKey="short_name"
          width={165}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: "#1E293B", fontWeight: 800 }}
        />
        <Tooltip
          cursor={{ fill: "rgba(99,102,241,0.04)" }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div style={{
                background: "#fff",
                border: "1px solid #E7ECF3",
                borderRadius: 12,
                padding: "10px 14px",
                fontSize: 12,
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                maxWidth: 320,
              }}>
                <div style={{ fontWeight: 950, color: "#0B1F33", marginBottom: 6 }}>
                  {shortProg(d.program_name)}
                </div>
                <div>AML Exposure Score: <b>{Number(d.exposure_score || 0).toFixed(1)}</b></div>
                <div>Flagged: <b>{int0(d.flagged_count)}</b> of <b>{int0(d.total_accounts)}</b> ({pct1(d.flagged_rate)})</div>
                <div>Review: <b>{int0(d.review_count)}</b> ({pct1(d.review_rate)})</div>
                <div>Critical / Block: <b>{int0(d.block_count)}</b> ({pct1(d.block_rate)})</div>
                <div style={{ marginTop: 6, color: "#6B7280" }}>
                Top Account-Level Rule: <b>{d.top_account_rule || "—"}</b>
                </div>
                <div style={{ color: "#6B7280" }}>
                Product Aggregate Signal: <b>{d.has_product_aggregate_signal ? "Yes" : "No"}</b>
                </div>
                <div style={{ color: "#6B7280" }}>
                Recommended Action: <b>{d.recommended_action || "Enhanced Monitoring"}</b>
                </div>
                            </div>
            );
          }}
        />
        <Bar
          dataKey="exposure_score"
          radius={[0, 5, 5, 0]}
          isAnimationActive={false}
          label={(props) => {
            const { x, y, width, height, index } = props;
            const d = programExposure[index];
            if (!d) return null;
          
            const score = Number(d.exposure_score || 0);
            const tier =
              score >= 80 ? "CRITICAL" :
              score >= 60 ? "HIGH" :
              score >= 40 ? "ELEVATED" :
              "WATCH";
          
            return (
              <text
                x={x + width + 8}
                y={y + height / 2}
                dy={4}
                fontSize={11}
                fontWeight={900}
                fill="#374151"
              >
                {tier} · {score.toFixed(1)}
              </text>
            );
          }}
        >
          {programExposure.map((p, i) => (
            <Cell
              key={i}
              fill={
                Number(p.exposure_score || 0) >= 80 ? "#B42318" :
                Number(p.exposure_score || 0) >= 60 ? "#DC2626" :
                Number(p.exposure_score || 0) >= 40 ? "#F59E0B" :
                "#3B82F6"
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )}
</div>

      {/* ══ RULE HITS CHART (reference image style) ══════════════════════════ */}
      <div style={{
        background: "#fff", border: "1px solid #E7ECF3", borderRadius: 16,
        padding: "26px 30px", marginBottom: SECTION_GAP, boxShadow: "0 4px 12px rgba(16,24,40,0.05)",
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 950, color: "#0B1F33" }}>
            AML Rule Hit Frequency Distribution (Grouped by Category)
          </div>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
            Frequency of rule hits · {periodLabel}
            {selectedProgram !== "All" ? ` · ${selectedProgram}` : ""}
          </div>
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>
            Click the grain badge next to each rule to switch between Account / Product / Identity drill-down.
          </div>
        </div>

        {/* Category legend (matches reference image) */}
        <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          {CATEGORY_ORDER.filter((c) => ruleChartData.some((r) => r.macro_category === c)).map((cat) => (
            <div key={cat} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: CATEGORY_COLORS[cat] }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{cat}</span>
            </div>
          ))}
        </div>

        {loadingRules ? (
          <div style={{ color: "#6B7280", fontSize: 13 }}>Loading rule hits…</div>
        ) : ruleChartData.length === 0 ? (
          <div style={{ color: "#6B7280", fontSize: 13 }}>No rule hits in this period.</div>
        ) : (
          <div onMouseDown={(e) => e.preventDefault()} style={{ outline: "none" }}>
          <ResponsiveContainer width="100%" height={Math.max(300, ruleChartData.length * 44)}>
            <BarChart
              data={ruleChartData}
              layout="vertical"
              margin={{ top: 0, right: 60, left: 270, bottom: 24 }}
              barCategoryGap="30%"
              style={{ outline: "none" }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F4F8" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 12, fill: "#6B7280" }}
                axisLine={{ stroke: "#E7ECF3" }}
                tickLine={false}
                label={{
                  value: "Frequency of Hits (Current Period)",
                  position: "insideBottom",
                  offset: -14,
                  fontSize: 12,
                  fill: "#6B7280",
                }}
              />
              <YAxis
                type="category"
                dataKey="rule_name"
                width={265}
                axisLine={false}
                tickLine={false}
                tick={(props) => (
                  <RuleAxisTick
                    {...props}
                    selectedRule={selectedRule}
                    clickable={false}
                    onSelect={() => {}}
                  />
                )}
              />
              <Tooltip content={<RuleTip portfolioMode />} cursor={{ fill: "rgba(99,102,241,0.04)" }} />
              <Bar
                dataKey="display_count" name="Count" radius={[0, 4, 4, 0]}
                style={{ cursor: "default" }}
                isAnimationActive={false}
                label={(props) => {
                  const { x, y, width, height, value, index } = props;
                  const entry = ruleChartData[index];
                  if (!entry) return null;
                  const rule = entry.rule_name;
                  const isExp = ruleBarExpanded === rule;
                  const numStr = value > 0 ? int0(value) : "";
                  const textX = x + width + 6;
                  const midY = y + height / 2;
                  const btnX = textX + (numStr ? numStr.length * 7 + 4 : 0);
                  return (
                    <g>
                      {numStr && <text x={textX} y={midY} dy={4} fontSize={11} fontWeight={700} fill="#374151">{numStr}</text>}
                      <g transform={`translate(${btnX},${midY - 9})`} style={{ cursor: "pointer" }}
                        onClick={(e) => { e.stopPropagation(); setRuleBarExpanded(isExp ? null : rule); }}>
                        <rect width={22} height={18} rx={4} fill={isExp ? "#1D4ED8" : "#EFF2FF"} />
                        <text x={11} y={12} textAnchor="middle" fontSize={9} fontWeight={800} fill={isExp ? "#fff" : "#4338CA"}>{isExp ? "▲" : "▶"}</text>
                      </g>
                    </g>
                  );
                }}
              >
                {ruleChartData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={CATEGORY_COLORS[entry.macro_category] || CATEGORY_COLORS.Other}
                    opacity={selectedRule && selectedRule !== entry.rule_name ? 0.35 : 1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          </div>
        )}

        {/* ── ▶ Program breakdown panel ─────────────────────────────────────── */}
        {ruleBarExpanded && (
          <div style={{
            marginTop: 16, background: "#F5F5F5", borderRadius: 12,
            border: "1px solid #E0E0E0", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "12px 20px", background: "#EBEBEB", borderBottom: "1px solid #DADADA",
            }}>
              <span style={{ fontSize: 14, fontWeight: 900, color: "#1A1A1A" }}>
                {ruleBarExpanded}: {ruleGrain(ruleBarExpanded) === "Product" ? "Product Breakdown" : "Program Breakdown"}
              </span>
              <button
                onClick={() => setRuleBarExpanded(null)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 900, color: "#555", display: "flex", alignItems: "center", gap: 4 }}
              >
                Close <span style={{ fontSize: 16, lineHeight: 1 }}>✕</span>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "20px 24px" }}>
              {ruleGrain(ruleBarExpanded) === "Product" ? (
                /* Rule 8: show flat product breakdown */
                ruleBarProductSummary.length === 0 ? (
                  <div style={{ color: "#6B7280", fontSize: 13 }}>Loading…</div>
                ) : (() => {
                  const maxCount = Math.max(...ruleBarProductSummary.map((p) => p.account_count), 1);
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: 440, overflowY: "auto" }}>
                      {ruleBarProductSummary.map((p) => {
                        const pct = (p.account_count / maxCount) * 100;
                        const barColor = pct >= 80 ? "#B91C1C" : pct >= 50 ? "#D97706" : "#3B82F6";
                        return (
                          <div key={p.product_name} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <div style={{ width: 200, flexShrink: 0, fontSize: 13, fontWeight: 800, color: "#1A1A1A", textAlign: "right" }}>
                              {p.product_name || "Unknown Product"}
                            </div>
                            <div style={{ flex: 1, background: "#DEDEDE", borderRadius: 4, height: 28, position: "relative", overflow: "hidden" }}>
                              <div style={{
                                width: `${Math.min(pct, 100)}%`, height: "100%",
                                background: barColor, borderRadius: 4,
                                display: "flex", alignItems: "center", paddingLeft: 8,
                                minWidth: p.account_count > 0 ? 48 : 0,
                              }}>
                                {p.account_count > 0 && (
                                  <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", whiteSpace: "nowrap" }}>
                                    {p.account_count.toLocaleString()} acct{p.account_count !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                /* All other rules: show program breakdown */
                loadingRuleProgSummary ? (
                  <div style={{ color: "#6B7280", fontSize: 13 }}>Loading…</div>
                ) : ruleProgramSummary.length === 0 ? (
                  <div style={{ color: "#6B7280", fontSize: 13 }}>No program data available.</div>
                ) : (() => {
                    const maxHit = Math.max(...ruleProgramSummary.map((p) => p.hit_count), 1);
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {ruleProgramSummary.map((p) => {
                          const pct = (p.hit_count / maxHit) * 100;
                          const barColor = pct >= 80 ? "#B91C1C" : pct >= 50 ? "#D97706" : "#3B82F6";
                          return (
                            <div key={p.program_name} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                              <div style={{ width: 160, flexShrink: 0, fontSize: 13, fontWeight: 800, color: "#1A1A1A", textAlign: "right" }}>
                                {shortProg(p.program_name)}
                              </div>
                              <div style={{ flex: 1, background: "#DEDEDE", borderRadius: 4, height: 28, overflow: "hidden" }}>
                                <div style={{
                                  width: `${Math.min(pct, 100)}%`, height: "100%",
                                  background: barColor, borderRadius: 4,
                                  display: "flex", alignItems: "center", paddingLeft: 8,
                                  minWidth: p.hit_count > 0 ? 60 : 0,
                                }}>
                                  {p.hit_count > 0 && (
                                    <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", whiteSpace: "nowrap" }}>
                                      {p.hit_count.toLocaleString()} acct{p.hit_count !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
              )}
            </div>
          </div>
        )}

        {/* ── Rule drill-down table ──────────────────────────────────────────── */}
        {selectedRule && (
          <div style={{
            marginTop: 20, borderTop: "1px solid #E7ECF3", paddingTop: 16,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 900, color: "#0B1F33" }}>
                  Accounts flagged by:
                </span>
                <span style={{
                  marginLeft: 8, fontSize: 13, fontWeight: 700,
                  color: CATEGORY_COLORS[ruleChartData.find((r) => r.rule_name === selectedRule)?.macro_category] || "#1D4ED8",
                }}>
                  {selectedRule}
                </span>
              </div>
              <button
                onClick={() => { setSelectedRule(null); setSelectedProduct(null); setExpandedAcct(null); }}
                style={{
                  padding: "4px 12px", borderRadius: 8, border: "1px solid #E7ECF3",
                  fontSize: 12, fontWeight: 700, cursor: "pointer", background: "#F8FAFC", color: "#374151",
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* ── Program summary for this rule ──────────────────────────── */}
            {loadingRuleProgSummary ? (
              <div style={{ color: "#6B7280", fontSize: 13, marginBottom: 16 }}>Loading summary…</div>
            ) : ruleProgramSummary.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#5E6B7A", marginBottom: 8 }}>
                  Accounts flagged per program
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {ruleProgramSummary.filter((p) =>
                    selectedProgram === "All" || p.program_name === selectedProgram
                  ).map((p) => {
                    const pct = p.total_accounts > 0 ? ((p.hit_count / p.total_accounts) * 100).toFixed(1) : 0;
                    const name = (p.program_name || "Unknown").replace("SSB Bank FBO ", "").replace(" Customers", "");
                    return (
                      <div key={p.program_name} style={{
                        background: "#F8FAFC", border: "1px solid #E7ECF3", borderRadius: 10,
                        padding: "8px 14px", display: "flex", flexDirection: "column", gap: 2,
                        minWidth: 160,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#5E6B7A" }}>{name}</div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                          <span style={{ fontSize: 20, fontWeight: 950, color: "#0B1F33" }}>
                            {p.hit_count.toLocaleString()}
                          </span>
                          <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}>
                            / {p.total_accounts.toLocaleString()}
                          </span>
                          <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Rule 8: product table → accounts UI ──────────────────── */}
            {IS_RULE8 ? (
              <div>
                {/* Product table — flat list across all programs */}
                {loadingRuleProduct ? (
                  <div style={{ color: "#6B7280", fontSize: 13 }}>Loading products…</div>
                ) : ruleProductBreakdown.length === 0 ? (
                  <div style={{ color: "#6B7280", fontSize: 13 }}>No flagged accounts for this rule in the period.</div>
                ) : (
                  <div>
                    {!selectedProduct?.product_name && (() => {
                      const flatProducts = ruleProductBreakdown.flatMap((prog) =>
                        (prog.products || []).map((prod) => ({ ...prod, program_name: prog.program_name }))
                      );
                      return (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#5E6B7A", marginBottom: 8 }}>
                            Click a product to view flagged accounts
                          </div>
                          {(() => {
                            const maxCount = Math.max(...flatProducts.map((p) => p.account_count), 1);
                            return (
                              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 440, overflowY: "auto" }}>
                                {flatProducts.map((prod) => {
                                  const pct = (prod.account_count / maxCount) * 100;
                                  const barColor = pct >= 80 ? "#B91C1C" : pct >= 50 ? "#D97706" : "#3B82F6";
                                  return (
                                    <div
                                      key={`${prod.program_name}__${prod.product_name}`}
                                      style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                                      onClick={() => { setExpandedAcct(null); setSelectedProduct({ program_name: prod.program_name, product_name: prod.product_name }); }}
                                    >
                                      <div style={{ width: 180, flexShrink: 0, fontSize: 13, fontWeight: 800, color: "#1A1A1A", textAlign: "right" }}>
                                        {prod.product_name || "Unknown Product"}
                                      </div>
                                      <div style={{ flex: 1, background: "#DEDEDE", borderRadius: 4, height: 28, overflow: "hidden" }}>
                                        <div style={{
                                          width: `${Math.min(pct, 100)}%`, height: "100%",
                                          background: barColor, borderRadius: 4,
                                          display: "flex", alignItems: "center", paddingLeft: 8,
                                          minWidth: prod.account_count > 0 ? 60 : 0,
                                        }}>
                                          {prod.account_count > 0 && (
                                            <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", whiteSpace: "nowrap" }}>
                                              {prod.account_count.toLocaleString()} acct{prod.account_count !== 1 ? "s" : ""}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Step 3: accounts table for selected product */}
                {selectedProduct?.product_name && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <button
                        onClick={() => { setExpandedAcct(null); setSelectedProduct(null); }}
                        style={{
                          padding: "3px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
                          border: "1.5px solid #E7ECF3", background: "#F8FAFC", color: "#374151", fontWeight: 700,
                        }}
                      >← Back to products</button>
                      <div style={{
                        flex: 1, padding: "5px 12px", borderRadius: 6,
                        background: "#FFFBEB", border: "1px solid #FDE68A",
                        fontSize: 12, color: "#78350F", fontWeight: 700,
                      }}>
                        {selectedProduct.product_name} — accounts flagged because this product's daily volume exceeded 150% of its 30-day average
                      </div>
                    </div>
                    {loadingRuleAccts ? (
                      <div style={{ color: "#6B7280", fontSize: 13 }}>Loading accounts…</div>
                    ) : ruleAccounts.length === 0 ? (
                      <div style={{ color: "#6B7280", fontSize: 13 }}>No flagged accounts for this product.</div>
                    ) : (
                      <div style={{ overflowX: "auto", maxHeight: 420 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr style={{ background: "#F8FAFC" }}>
                              {["Account", "Last Flagged", "Decision", "Score", "Fired Rules"].map((h) => (
                                <th key={h} style={{
                                  padding: "8px 12px", textAlign: "left", fontWeight: 900,
                                  color: "#374151", borderBottom: "2px solid #E7ECF3", whiteSpace: "nowrap",
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedRuleAccounts.map((a, i) => {
                              const ruleKey = `${a.account}__rule`;
                              const isExp = expandedAcct?.account === a.account;
                              return (
                                <React.Fragment key={ruleKey}>
                                  <tr
                                    onClick={() => setExpandedAcct(isExp ? null : { account: a.account, ref_date: a.ref_date, rule_name: selectedRule, date_from: dateFrom, date_to: dateTo })}
                                    style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFC", cursor: "pointer" }}
                                  >
                                    <td style={{ padding: "7px 12px", fontFamily: "monospace", fontWeight: 700, color: "#1D4ED8" }}>
                                      {isExp ? "▲ " : "▼ "}{a.account}
                                      {a.flag_count > 1 && (
                                        <span style={{ marginLeft: 6, fontSize: 10, fontFamily: "sans-serif", background: "#DBEAFE", color: "#1D4ED8", borderRadius: 10, padding: "1px 6px", fontWeight: 700 }}>
                                          ×{a.flag_count}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: "7px 12px", color: "#6B7280" }}>{a.ref_date}</td>
                                    <td style={{ padding: "7px 12px" }}>
                                      {a.result === "BLOCK" ? <Pill text="Block" tone="bad" /> : <Pill text="Review" tone="warn" />}
                                    </td>
                                    <td style={{ padding: "7px 12px", fontWeight: 800, color: "#0B1F33" }}>
                                      {a.score != null ? Number(a.score).toFixed(1) : "—"}
                                    </td>
                                    <td style={{ padding: "7px 12px", color: "#374151", maxWidth: 300, wordBreak: "break-word" }}>
                                      {a.fired_rules}
                                    </td>
                                  </tr>
                                  {isExp && (
                                    <tr style={{ background: "#F0F6FF" }}>
                                      <td colSpan={5} style={{ padding: "12px 24px" }}>
                                        <div style={{ fontSize: 12, fontWeight: 800, color: "#1D4ED8", marginBottom: 8 }}>
                                          Transactions matching "{selectedRule}" · {periodLabel}
                                        </div>
                                        {loadingAcctTxns ? (
                                          <div style={{ color: "#6B7280", fontSize: 12 }}>Loading…</div>
                                        ) : acctTxns.length === 0 ? (
                                          <div style={{ color: "#6B7280", fontSize: 12 }}>No matching transactions found.</div>
                                        ) : (
                                          <div style={{ overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                              <thead>
                                                <tr style={{ background: "#DBEAFE" }}>
                                                  {["Date/Time", "Amount", "Direction", "Type", "Counterparty", "Country", "Description"].map((h) => (
                                                    <th key={h} style={{
                                                      padding: "6px 10px", textAlign: "left", fontWeight: 800,
                                                      color: "#1E3A5F", borderBottom: "1.5px solid #BFDBFE", whiteSpace: "nowrap",
                                                    }}>{h}</th>
                                                  ))}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {acctTxns.map((t, ti) => (
                                                  <tr key={ti} style={{ background: ti % 2 === 0 ? "#EFF6FF" : "#F8FAFC" }}>
                                                    <td style={{ padding: "5px 10px", whiteSpace: "nowrap", color: "#374151" }}>
                                                      {t.post_date?.replace("T", " ").replace("+00", "").slice(0, 16)}
                                                    </td>
                                                    <td style={{ padding: "5px 10px", fontWeight: 800, color: "#0B1F33", whiteSpace: "nowrap" }}>
                                                      ${Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td style={{ padding: "5px 10px" }}>
                                                      <span style={{
                                                        padding: "2px 7px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                                                        background: t.direction === "INBOUND" || t.direction === "CREDIT" ? "#DCFCE7" : "#FEE2E2",
                                                        color: t.direction === "INBOUND" || t.direction === "CREDIT" ? "#166534" : "#991B1B",
                                                      }}>{t.direction}</span>
                                                    </td>
                                                    <td style={{ padding: "5px 10px", color: "#374151", whiteSpace: "nowrap" }}>{t.transaction_code || "—"}</td>
                                                    <td style={{ padding: "5px 10px", color: "#374151", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                        title={t.counterparty_name}>{t.counterparty_name || "—"}</td>
                                                    <td style={{ padding: "5px 10px", color: "#6B7280" }}>{t.counterparty_country_code || "—"}</td>
                                                    <td style={{ padding: "5px 10px", color: "#6B7280", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                        title={t.description}>{t.description || "—"}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
                                              {acctTxns.length} transaction{acctTxns.length !== 1 ? "s" : ""} matching rule criteria
                                            </div>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* ── All other rules: flat accounts table ─────────────────── */
              loadingRuleAccts ? (
                <div style={{ color: "#6B7280", fontSize: 13 }}>Loading accounts…</div>
              ) : ruleAccounts.length === 0 ? (
                <div style={{ color: "#6B7280", fontSize: 13 }}>No flagged accounts found for this rule in the period.</div>
              ) : (
                <>
                <div style={{ overflowX: "auto", maxHeight: 380 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr style={{ background: "#F8FAFC" }}>
                        {["Account", "Program", "Last Flagged", "Decision", "Score", "Fired Rules"].map((h) => {
                          const col = h === "Program" ? "program" : h === "Last Flagged" ? "date" : null;
                          const isActive = ruleSort.col === col;
                          return (
                            <th key={h}
                              onClick={col ? () => toggleRuleSort(col) : undefined}
                              style={{
                                padding: "8px 12px", textAlign: "left", fontWeight: 900,
                                color: isActive ? "#1D4ED8" : "#374151",
                                borderBottom: "2px solid #E7ECF3", whiteSpace: "nowrap",
                                cursor: col ? "pointer" : "default", userSelect: "none",
                              }}>
                              {h}{col ? (isActive ? (ruleSort.dir === "asc" ? " ▲" : " ▼") : " ⇅") : ""}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRuleAccounts.map((a, i) => {
                        const ruleKey = `${a.account}__rule`;
                        const isExp = expandedAcct?.account === a.account;
                        return (
                          <React.Fragment key={ruleKey}>
                            <tr
                              onClick={() => setExpandedAcct(isExp ? null : { account: a.account, ref_date: a.ref_date, rule_name: selectedRule, date_from: dateFrom, date_to: dateTo })}
                              style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFC", cursor: "pointer" }}
                            >
                              <td style={{ padding: "7px 12px", fontFamily: "monospace", fontWeight: 700, color: "#1D4ED8" }}>
                                {isExp ? "▲ " : "▼ "}{a.account}
                                {a.flag_count > 1 && (
                                  <span style={{ marginLeft: 6, fontSize: 10, fontFamily: "sans-serif", background: "#DBEAFE", color: "#1D4ED8", borderRadius: 10, padding: "1px 6px", fontWeight: 700 }}>
                                    ×{a.flag_count}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "7px 12px", color: "#374151" }}>
                                {(a.program_name || "—").replace("SSB Bank FBO ", "").replace(" Customers", "")}
                              </td>
                              <td style={{ padding: "7px 12px", color: "#6B7280" }}>{a.ref_date}</td>
                              <td style={{ padding: "7px 12px" }}>
                                {a.result === "BLOCK" ? <Pill text="Block" tone="bad" /> : <Pill text="Review" tone="warn" />}
                              </td>
                              <td style={{ padding: "7px 12px", fontWeight: 800, color: "#0B1F33" }}>
                                {a.score != null ? Number(a.score).toFixed(1) : "—"}
                              </td>
                              <td style={{ padding: "7px 12px", color: "#374151", maxWidth: 300, wordBreak: "break-word" }}>
                                {a.fired_rules}
                              </td>
                            </tr>
                            {isExp && (
                              <tr style={{ background: "#F0F6FF" }}>
                                <td colSpan={6} style={{ padding: "12px 24px" }}>
                                  <div style={{ fontSize: 12, fontWeight: 800, color: "#1D4ED8", marginBottom: 8 }}>
                                    Transactions matching "{selectedRule}" · {periodLabel}
                                  </div>
                                  {loadingAcctTxns ? (
                                    <div style={{ color: "#6B7280", fontSize: 12 }}>Loading…</div>
                                  ) : acctTxns.length === 0 ? (
                                    <div style={{ color: "#6B7280", fontSize: 12 }}>No matching transactions found.</div>
                                  ) : (
                                    <div style={{ overflowX: "auto" }}>
                                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                        <thead>
                                          <tr style={{ background: "#DBEAFE" }}>
                                            {["Date/Time", "Amount", "Direction", "Type", "Counterparty", "Country", "Description"].map((h) => (
                                              <th key={h} style={{
                                                padding: "6px 10px", textAlign: "left", fontWeight: 800,
                                                color: "#1E3A5F", borderBottom: "1.5px solid #BFDBFE", whiteSpace: "nowrap",
                                              }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {acctTxns.map((t, ti) => (
                                            <tr key={ti} style={{ background: ti % 2 === 0 ? "#EFF6FF" : "#F8FAFC" }}>
                                              <td style={{ padding: "5px 10px", whiteSpace: "nowrap", color: "#374151" }}>
                                                {t.post_date?.replace("T", " ").replace("+00", "").slice(0, 16)}
                                              </td>
                                              <td style={{ padding: "5px 10px", fontWeight: 800, color: "#0B1F33", whiteSpace: "nowrap" }}>
                                                ${Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </td>
                                              <td style={{ padding: "5px 10px" }}>
                                                <span style={{
                                                  padding: "2px 7px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                                                  background: t.direction === "INBOUND" || t.direction === "CREDIT" ? "#DCFCE7" : "#FEE2E2",
                                                  color: t.direction === "INBOUND" || t.direction === "CREDIT" ? "#166534" : "#991B1B",
                                                }}>{t.direction}</span>
                                              </td>
                                              <td style={{ padding: "5px 10px", color: "#374151", whiteSpace: "nowrap" }}>{t.transaction_code || "—"}</td>
                                              <td style={{ padding: "5px 10px", color: "#374151", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                  title={t.counterparty_name}>{t.counterparty_name || "—"}</td>
                                              <td style={{ padding: "5px 10px", color: "#6B7280" }}>{t.counterparty_country_code || "—"}</td>
                                              <td style={{ padding: "5px 10px", color: "#6B7280", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                  title={t.description}>{t.description || "—"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
                                        {acctTxns.length} transaction{acctTxns.length !== 1 ? "s" : ""}
                                        {acctTxnFallback === "all_in_period" ? " · no rule-specific match; showing all in period" : acctTxnFallback === "extended_window" ? " · no exact match; showing 30-day window" : acctTxnFallback === "nearest_activity" ? " · no rule match; showing nearest activity" : " matching rule criteria"}
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  page={ruleAcctPage}
                  hasMore={ruleAcctHasMore}
                  loading={loadingRuleAccts}
                  count={ruleAccounts.length}
                  onPrev={() => setRuleAcctPage((p) => Math.max(0, p - 1))}
                  onNext={() => setRuleAcctPage((p) => p + 1)}
                />
                </>
              )
            )}
          </div>
        )}
      </div>

      {/* ── Program Breakdown Table (All Programs only) ─────────────────────── */}
      {/* ── Program Exposure Detail Table ─────────────────────────────────────── */}
{selectedProgram === "All" && (
  <div style={{
    background: "#fff",
    border: "1px solid #E7ECF3",
    borderRadius: 16,
    padding: 22,
    marginBottom: SECTION_GAP,
    boxShadow: "0 4px 12px rgba(16,24,40,0.05)",
  }}>
    <div style={{ fontSize: 13, fontWeight: 900, color: "#0B1F33", marginBottom: 4 }}>
      Program Exposure Details · {periodLabel}
    </div>
    <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>
      Ranked by AML exposure score. Review/block rates show which programs need attention.
    </div>

    {loadingExposure ? (
      <div style={{ color: "#6B7280", fontSize: 13 }}>Loading program exposure…</div>
    ) : programExposure.length === 0 ? (
      <div style={{ color: "#6B7280", fontSize: 13 }}>No program exposure data for this period.</div>
    ) : (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#F8FAFC" }}>
            {["Rank", "Program", "AML Exposure Score", "Flagged Accounts", "Review", "Critical / Block", "Top Account Rule", "Recommended Action"].map((h) => (
                <th key={h} style={{
                  padding: "9px 12px",
                  textAlign: ["Program", "Top Account Rule", "Recommended Action"].includes(h) ? "left" : "right",
                  fontWeight: 900,
                  color: "#374151",
                  borderBottom: "2px solid #E7ECF3",
                  whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {programExposure.map((p, i) => {
              const score = Number(p.exposure_score || 0);
              const tier =
                score >= 80 ? "Critical" :
                score >= 60 ? "High" :
                score >= 40 ? "Elevated" :
                "Moderate";

              const tierColor =
                score >= 80 ? "#B42318" :
                score >= 60 ? "#DC2626" :
                score >= 40 ? "#D97706" :
                "#1D4ED8";

              return (
                <tr key={p.program_name} style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFC" }}>
                  <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 900, color: "#64748B" }}>
                    {i + 1}
                  </td>

                  <td style={{ padding: "9px 12px", fontWeight: 900, color: "#0B1F33" }}>
                    {shortProg(p.program_name)}
                  </td>

                  <td style={{ padding: "9px 12px", textAlign: "right" }}>
                    <span style={{ fontWeight: 950, color: tierColor, fontSize: 15 }}>
                      {score.toFixed(1)}
                    </span>
                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 900, color: tierColor }}>
                      {tier}
                    </span>
                  </td>

                  <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800 }}>
                    {int0(p.flagged_count)} / {int0(p.total_accounts)} accounts
                  </td>

                  <td style={{ padding: "9px 12px", textAlign: "right", color: "#D97706", fontWeight: 800 }}>
                    {pct1(p.review_rate)}
                  </td>

                  <td style={{ padding: "9px 12px", textAlign: "right", color: "#DC2626", fontWeight: 800 }}>
                    {pct1(p.block_rate)}
                  </td>

                  <td style={{ padding: "9px 12px", color: "#374151", fontWeight: 700 }}>
                    {p.top_account_rule || "—"}
                    </td>

                    <td style={{ padding: "9px 12px", color: "#374151", fontWeight: 800 }}>
                    {p.recommended_action || "Enhanced Monitoring"}
                    </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

      </>)} {/* end Portfolio Overview */}

      {/* ══════════════════════ PROGRAM INSIGHTS TAB ════════════════════════ */}
      {activeTab === "insights" && (
        <>
          <div style={{
            ...CARD_STYLE,
            padding: "16px 20px",
            marginBottom: SECTION_GAP,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 14,
            alignItems: "stretch",
          }}>
            {[
              ["Scope", executiveInsight.scope],
              ["Flagged Accounts", int0(executiveInsight.flaggedAccounts)],
              ["Top Rule", executiveInsight.topRule],
              ["Highest-Risk Program", `${executiveInsight.highestRiskProgram}${executiveInsight.highestRiskFlags ? ` · ${int0(executiveInsight.highestRiskFlags)} flags` : ""}`],
            ].map(([label, value]) => (
              <div key={label} style={{
                background: "#F8FAFC",
                border: "1px solid #E7ECF3",
                borderRadius: 14,
                padding: "12px 14px",
                minWidth: 0,
              }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                  {label}
                </div>
                <div title={String(value)} style={{
                  fontSize: 13,
                  fontWeight: 900,
                  color: "#0B1F33",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 18, alignItems: "flex-start", width: "100%", overflow: "hidden" }}>

          {/* ── Left: Program tree ────────────────────────────────────────── */}
          <div style={{
            width: Math.min(leftWidth, 380), maxWidth: "42%", flexShrink: 0,
            background: "#fff", border: "1px solid #E7ECF3", borderRadius: 16,
            boxShadow: "0 4px 12px rgba(16,24,40,0.05)", overflow: "hidden",
          }}>
            {/* Tree root */}
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #E7ECF3", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13, color: "#6B7280" }}>∨</span>
              <span style={{ fontSize: 13, fontWeight: 900, color: "#0B1F33" }}>Programs</span>
            </div>

            {loadingProg ? (
              <div style={{ padding: 16, color: "#6B7280", fontSize: 13 }}>Loading…</div>
            ) : programs.length === 0 ? (
              <div style={{ padding: 16, color: "#6B7280", fontSize: 13 }}>No data.</div>
            ) : (
              <div style={{ overflowY: "auto", maxHeight: 700 }}>
                {programs.map((p, pi) => {
                  const isLast = pi === programs.length - 1;
                  const flags = programDistinctAccounts[p.program_name] ?? ((p.review || 0) + (p.block || 0));
                  const isExpanded = expandedPrograms.has(p.program_name);
                  const isActive = insightsProgram === p.program_name;
                  const products = programProductTree[p.program_name] || {};
                  const productEntries = Object.entries(products).sort((a, b) => b[1].count - a[1].count);
                  const LINE = "#D1D5DB";
                  const ROW_H = 36; // px, approximate row height for line calc

                  const toggleExpand = () => setExpandedPrograms((prev) => {
                    const next = new Set(prev);
                    next.has(p.program_name) ? next.delete(p.program_name) : next.add(p.program_name);
                    return next;
                  });

                  // Count child rows for vertical line height
                  const childRows = isExpanded ? (1 + (flags > 0 ? 1 : 0) + productEntries.length) : 0;

                  return (
                    <div key={p.program_name} style={{ position: "relative" }}>

                      {/* ── Program row ─────────────────────────────────── */}
                      <div
                        style={{
                          display: "flex", alignItems: "center",
                          background: isActive ? "#EFF6FF" : "transparent",
                          cursor: "pointer", position: "relative", height: ROW_H,
                        }}
                        onClick={() => {
                          setInsightsProgram(isActive ? null : p.program_name);
                          setInsightsProduct(null);
                          setInsightsAccount(null);
                          setInsightsSelectedRule(null);
                          setInsightsTxnRow(null);
                          if (!isExpanded) toggleExpand();
                        }}
                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#F8FAFC"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? "#EFF6FF" : "transparent"; }}
                      >
                        {/* Gutter: tree line column (16px wide) */}
                        <div style={{ width: 16, flexShrink: 0, alignSelf: "stretch", position: "relative" }}>
                          {/* Vertical line going down (to children or next sibling) */}
                          {!isLast && (
                            <div style={{ position: "absolute", left: 7, top: "50%", bottom: -(childRows * ROW_H), width: 1, background: LINE }} />
                          )}
                          {/* Horizontal connector: ─ */}
                          <div style={{ position: "absolute", left: 7, top: "50%", width: 9, height: 1, background: LINE }} />
                        </div>

                        {/* +/− button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
                          style={{
                            width: 16, height: 16, borderRadius: 2, border: "1.5px solid #9CA3AF",
                            background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 900,
                            color: "#374151", lineHeight: 1, padding: 0, flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >{isExpanded ? "−" : "+"}</button>

                        {/* Label */}
                        <div style={{
                          flex: 1, minWidth: 0, padding: "0 10px 0 6px",
                          fontSize: 12, fontWeight: 800,
                          color: isActive ? "#1D4ED8" : "#111827",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          [Program] {shortProg(p.program_name)}
                        </div>
                      </div>

                      {/* ── Children (exception summary + products) ──────── */}
                      {isExpanded && (
                        <div style={{ position: "relative" }}>
                          {/* Vertical line from program down through all children */}
                          {!isLast && (
                            <div style={{ position: "absolute", left: 7, top: 0, bottom: 0, width: 1, background: LINE }} />
                          )}

                          {/* Exception Summary */}
                          <div style={{
                            display: "flex", alignItems: "center", height: ROW_H,
                            background: isActive ? "#EFF6FF" : "#FAFBFC", cursor: "default",
                            position: "relative",
                          }}>
                            {/* Indent gutter (16px) + connector */}
                            <div style={{ width: 16, flexShrink: 0 }} />
                            {/* L-shaped connector from parent's vertical line */}
                            <div style={{ width: 20, flexShrink: 0, alignSelf: "stretch", position: "relative" }}>
                              {/* vertical part of L */}
                              {productEntries.length > 0 && (
                                <div style={{ position: "absolute", left: 7, top: 0, bottom: 0, width: 1, background: LINE }} />
                              )}
                              {/* horizontal part of L */}
                              <div style={{ position: "absolute", left: 7, top: "50%", width: 9, height: 1, background: LINE }} />
                            </div>
                            {/* − icon */}
                            <span style={{ fontSize: 11, fontWeight: 800, color: "#9CA3AF", marginRight: 5, flexShrink: 0 }}>−</span>
                            {/* Label + bar */}
                            <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: "#B45309" }}>
                                Exception Summary: {int0(flags)} Flags
                              </div>
                              {p.total > 0 && (
                                <div style={{ marginTop: 3, height: 5, borderRadius: 3, background: "#E5E7EB", overflow: "hidden", display: "flex", width: "80%" }}>
                                  <div style={{ width: `${safePct(p.review, p.total)}%`, background: "#F59E0B" }} />
                                  <div style={{ width: `${safePct(p.block, p.total)}%`, background: "#EF4444" }} />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Product rows */}
                          {productEntries.map(([prod, prodData], qi) => {
                            const isLastProd = qi === productEntries.length - 1;
                            const isProdActive = insightsProduct === prod && insightsProgram === p.program_name;
                            const prodAccounts = prodData.accounts || [];
                            const cnt = prodData.count;
                            return (
                              <div key={prod}>
                                {/* Product row */}
                                <div
                                  onClick={() => {
                                    setInsightsProgram(p.program_name);
                                    setInsightsProduct(isProdActive ? null : prod);
                                    setInsightsAccount(null);
                                    setInsightsSelectedRule(null);
                                    setInsightsTxnRow(null);
                                  }}
                                  style={{
                                    display: "flex", alignItems: "center", height: ROW_H,
                                    background: isProdActive ? "#EFF6FF" : "#FAFBFC",
                                    cursor: "pointer", position: "relative",
                                  }}
                                  onMouseEnter={(e) => { if (!isProdActive) e.currentTarget.style.background = "#F1F5F9"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = isProdActive ? "#EFF6FF" : "#FAFBFC"; }}
                                >
                                  <div style={{ width: 16, flexShrink: 0 }} />
                                  <div style={{ width: 20, flexShrink: 0, alignSelf: "stretch", position: "relative" }}>
                                    {(!isLastProd || isProdActive) && (
                                      <div style={{ position: "absolute", left: 7, top: 0, bottom: 0, width: 1, background: LINE }} />
                                    )}
                                    <div style={{ position: "absolute", left: 7, top: "50%", width: 9, height: 1, background: LINE }} />
                                  </div>
                                  <span style={{ fontSize: 10, fontWeight: 800, color: isProdActive ? "#1D4ED8" : "#9CA3AF", marginRight: 5, flexShrink: 0 }}>{isProdActive ? "−" : "+"}</span>
                                  <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: isProdActive ? "#1D4ED8" : "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      [Product] {prod}:
                                      <span style={{ color: "#D97706", fontWeight: 800, marginLeft: 4 }}>{int0(cnt)} Flags</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Flagged accounts under this product */}
                                {isProdActive && prodAccounts.length > 0 && (
                                  <div style={{
                                    maxHeight: 5 * 28, overflowY: prodAccounts.length > 5 ? "auto" : "visible",
                                    background: "#F8FAFC", borderBottom: "1px solid #F1F5F9",
                                  }}>
                                    {prodAccounts.map((acct, ai) => {
                                      const isAcctActive = insightsAccount === acct.account;
                                      return (
                                      <div key={`${acct.account}__${acct.ref_date}`}
                                        onClick={() => { setInsightsAccount(isAcctActive ? null : acct.account); setInsightsSelectedRule(null); setInsightsTxnRow(null); }}
                                        style={{
                                          display: "flex", alignItems: "center", height: 28,
                                          paddingLeft: 56, paddingRight: 10, gap: 6,
                                          borderTop: "1px solid #F1F5F9", cursor: "pointer",
                                          background: isAcctActive ? "#EFF6FF" : ai % 2 === 0 ? "#F8FAFC" : "#F1F5F9",
                                        }}
                                        onMouseEnter={(e) => { if (!isAcctActive) e.currentTarget.style.background = "#E0EAFF"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = isAcctActive ? "#EFF6FF" : ai % 2 === 0 ? "#F8FAFC" : "#F1F5F9"; }}
                                      >
                                        <div style={{ width: 12, flexShrink: 0, position: "relative", alignSelf: "stretch" }}>
                                          {ai < prodAccounts.length - 1 && (
                                            <div style={{ position: "absolute", left: 5, top: 0, bottom: 0, width: 1, background: LINE }} />
                                          )}
                                          <div style={{ position: "absolute", left: 5, top: "50%", width: 7, height: 1, background: LINE }} />
                                        </div>
                                        <span style={{ fontFamily: "monospace", fontSize: 10, fontWeight: 700, color: isAcctActive ? "#1D4ED8" : "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {acct.account}
                                        </span>
                                        <span style={{
                                          fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 999,
                                          background: acct.result === "BLOCK" ? "#FEE2E2" : "#FEF3C7",
                                          color: acct.result === "BLOCK" ? "#991B1B" : "#92400E",
                                          flexShrink: 0,
                                        }}>{acct.result === "BLOCK" ? "Block" : "Review"}</span>
                                      </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Drag handle ──────────────────────────────────────────────── */}
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              const startX = e.clientX;
              const startW = leftWidth;
              const onMove = (ev) => setLeftWidth(Math.max(280, Math.min(380, startW + ev.clientX - startX)));
              const onUp   = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
              window.addEventListener("mousemove", onMove);
              window.addEventListener("mouseup", onUp);
            }}
            style={{
              width: 14, flexShrink: 0, cursor: "col-resize",
              display: "flex", alignItems: "center", justifyContent: "center",
              position: "relative", zIndex: 10,
            }}
          >
            {/* Visible indicator line */}
            <div style={{
              width: 3, height: "100%", minHeight: 40, borderRadius: 99,
              background: "#CBD5E1", transition: "background 0.15s",
            }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#94A3B8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#CBD5E1"; }}
            />
          </div>

          {/* ── Right: Rule chart + accounts ─────────────────────────────── */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            {!insightsProgram ? (
              <div style={{
                ...CARD_STYLE,
                padding: "42px 36px",
                textAlign: "center",
                color: "#64748B",
                fontSize: 14,
                minHeight: 260,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: 18,
                  background: "#EFF6FF",
                  color: "#1D4ED8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  fontWeight: 950,
                  marginBottom: 14,
                }}>↗</div>
                <div style={{ fontSize: 18, fontWeight: 950, color: "#0B1F33", marginBottom: 8 }}>
                  Select a program to inspect AML exceptions
                </div>
                <div style={{ maxWidth: 520, lineHeight: 1.55 }}>
                  Choose a program on the left to review product-level exceptions, flagged accounts, rule hits, and supporting transaction evidence for the selected period.
                </div>
              </div>
            ) : (
              <>
                {/* Rule hit chart scoped to insightsProduct or insightsProgram */}
                {(() => {
                  const rawChartData = insightsAccountRuleHits ?? insightsProductRuleHits ?? insightsRuleHits;
                  const chartData = rawChartData.map((r) => {
                    const g = ruleGrain(r.rule_name);
                    let display_count;
                    let display_grain = g;
                    if (g === "Product") {
                      if (insightsProduct) {
                        display_count = r.hit_count;
                        display_grain = "Account";
                      } else {
                        display_count = r.distinct_products ?? r.hit_count;
                      }
                    } else if (g === "Program") display_count = r.distinct_programs ?? r.hit_count;
                    else if (g === "Identity") display_count = r.distinct_accounts ?? r.hit_count;
                    else display_count = r.hit_count;
                    return { ...r, display_count, display_grain };
                  });
                  const grainLookup = Object.fromEntries(chartData.map((r) => [r.rule_name, r.display_grain]));
                  const chartTitle = insightsAccount
                    ? `Rule Hit Frequency — Account ${insightsAccount}`
                    : insightsProduct
                    ? `Rule Hit Frequency — ${insightsProduct}`
                    : `Rule Hit Frequency — ${shortProg(insightsProgram)} (Mixed Signals)`;
                  const noDataMsg = insightsAccount
                    ? "No rule hits for this account in the period."
                    : insightsProduct
                    ? "No rule hits for this product in the period."
                    : "No rule hits for this program in the period.";
                  return (
                <div style={{
                  background: "#fff", border: "1px solid #E7ECF3", borderRadius: 16,
                  padding: "26px 30px", marginBottom: SECTION_GAP,
                  boxShadow: "0 4px 12px rgba(16,24,40,0.05)",
                }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#0B1F33", marginBottom: 4 }}>
                    {chartTitle}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>
                    Click a rule name to view flagged accounts below
                  </div>

                  {loadingInsightsRules && !insightsProduct ? (
                    <div style={{ color: "#6B7280", fontSize: 13 }}>Loading…</div>
                  ) : chartData.length === 0 ? (
                    <div style={{ color: "#6B7280", fontSize: 13 }}>{noDataMsg}</div>
                  ) : (
                    <div onMouseDown={(e) => e.preventDefault()}>
                      <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 44)}>
                        <BarChart
                          data={chartData}
                          layout="vertical"
                          margin={{ top: 0, right: 180, left: 160, bottom: 0 }}
                          barCategoryGap="30%"
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F4F8" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={{ stroke: "#E7ECF3" }} tickLine={false} />
                          <YAxis
                            type="category" dataKey="rule_name" width={200}
                            axisLine={false} tickLine={false}
                            tick={(props) => (
                              <RuleAxisTick
                                {...props}
                                selectedRule={insightsSelectedRule}
                                onSelect={(name) => {
                                  setInsightsSelectedRule((prev) => prev === name ? null : name);
                                  setInsightsTxnRow(null);
                                }}
                                grainFn={(name) => grainLookup[name] || ruleGrain(name)}
                              />
                            )}
                          />
                          <Tooltip content={<RuleTip />} cursor={{ fill: "rgba(99,102,241,0.04)" }} />
                          <Bar
                            dataKey="display_count" radius={[0, 4, 4, 0]} isAnimationActive={false}
                            label={(props) => {
                              const { x, y, width, height, value, index } = props;
                              const entry = chartData[index];
                              if (!entry || value <= 0) return null;
                              const isRule8AtProgram = ruleGrain(entry.rule_name) === "Product" && !insightsProduct;
                              const riskEvents = entry.total_rule_firings ?? entry.hit_count ?? value;
                              const label = isRule8AtProgram
                                ? `${int0(value)} Products / ${int0(riskEvents)} Risk Events`
                                : `${int0(value)} Accts / ${int0(riskEvents)} Risk Events`;
                              return (
                                <text
                                  x={x + width + 6} y={y + height / 2} dy={4}
                                  fontSize={11} fontWeight={700} fill="#374151"
                                >
                                  {label}
                                </text>
                              );
                            }}
                          >
                            {chartData.map((entry, i) => (
                              <Cell key={i}
                                fill={CATEGORY_COLORS[entry.macro_category] || CATEGORY_COLORS.Other}
                                opacity={insightsSelectedRule && insightsSelectedRule !== entry.rule_name ? 0.35 : 1}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
                  );
                })()}

                {/* Accounts table for selected rule */}
                {insightsSelectedRule && (
                  <div style={{
                    background: "#fff", border: "1px solid #E7ECF3", borderRadius: 16,
                    padding: "22px 26px", boxShadow: "0 4px 12px rgba(16,24,40,0.05)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "#0B1F33" }}>
                        Rule Fired — <span style={{ color: "#1D4ED8" }}>{insightsSelectedRule}</span>
                        <div style={{ fontSize: 12, fontWeight: 500, color: "#6B7280", marginTop: 2 }}>Flagged Accounts</div>
                      </div>
                      <button
                        onClick={() => { setInsightsSelectedRule(null); setInsightsTxnRow(null); }}
                        style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid #E7ECF3", fontSize: 12, fontWeight: 700, cursor: "pointer", background: "#F8FAFC", color: "#374151" }}
                      >✕ Close</button>
                    </div>

                    {loadingInsightsAccts ? (
                      <div style={{ color: "#6B7280", fontSize: 13 }}>Loading accounts…</div>
                    ) : insightsAccounts.length === 0 ? (
                      <div style={{ color: "#6B7280", fontSize: 13 }}>No flagged accounts for this rule in the selected period.</div>
                    ) : (
                      <>
                      <div style={{ overflowX: "auto", maxHeight: 480 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr style={{ background: "#F8FAFC" }}>
                              {[
                                { label: "Program",        col: null },
                                { label: "Product",        col: "product" },
                                { label: "Account",        col: null },
                                { label: "Account Holder", col: null },
                                { label: "Report Date",    col: "ref_date" },
                                { label: "Status",         col: "status" },
                                { label: "View Details",   col: null },
                              ].map(({ label, col }) => (
                                <th key={label}
                                  onClick={col ? () => toggleInsightsSort(col) : undefined}
                                  style={{
                                    padding: "8px 12px", textAlign: "left", fontWeight: 900, whiteSpace: "nowrap",
                                    borderBottom: "2px solid #E7ECF3", userSelect: "none",
                                    cursor: col ? "pointer" : "default",
                                    color: insightsSort.col === col ? "#1D4ED8" : "#374151",
                                  }}>
                                  {label}{col ? (insightsSort.col === col ? (insightsSort.dir === "asc" ? " ▲" : " ▼") : " ⇅") : ""}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedInsightsAccounts.map((a, i) => {
                              const isExp = insightsTxnRow?.account === a.account && insightsTxnRow?.ref_date === a.ref_date;
                              return (
                                <React.Fragment key={`${a.account}__${a.ref_date}`}>
                                  <tr style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFC" }}>
                                    <td style={{ padding: "7px 12px", color: "#374151", fontSize: 12 }}>{shortProg(a.program_name)}</td>
                                    <td style={{ padding: "7px 12px", color: "#374151", fontSize: 12 }}>{a.product_name || "—"}</td>
                                    <td style={{ padding: "7px 12px", fontFamily: "monospace", fontWeight: 700, color: "#0B1F33" }}>{a.account}</td>
                                    <td style={{ padding: "7px 12px", color: "#374151", fontSize: 12 }}>{a.account_holder || "—"}</td>
                                    <td style={{ padding: "7px 12px", color: "#6B7280" }}>{a.ref_date}</td>
                                    <td style={{ padding: "7px 12px" }}>
                                      {a.result === "BLOCK" ? <Pill text="Block" tone="bad" /> : <Pill text="Review" tone="warn" />}
                                    </td>
                                    <td style={{ padding: "7px 12px" }}>
                                      <button
                                        onClick={() => setInsightsTxnRow(isExp ? null : { account: a.account, ref_date: a.ref_date, rule_name: insightsSelectedRule })}
                                        style={{
                                          padding: "3px 10px", borderRadius: 6, border: "1.5px solid",
                                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                                          background: isExp ? "#1D4ED8" : "#F1F5FF",
                                          color: isExp ? "#fff" : "#1D4ED8",
                                          borderColor: isExp ? "#1D4ED8" : "#BFDBFE",
                                        }}
                                      >{isExp ? "▲ Hide" : "▼ View Details"}</button>
                                    </td>
                                  </tr>
                                  {isExp && (
                                    <tr style={{ background: "#F0F6FF" }}>
                                      <td colSpan={7} style={{ padding: "14px 24px" }}>
                                        <div style={{ fontSize: 12, fontWeight: 800, color: "#1D4ED8", marginBottom: 8 }}>
                                          Transactions matching "{insightsSelectedRule}" · {periodLabel}
                                        </div>
                                        {loadingInsightsTxn ? (
                                          <div style={{ color: "#6B7280", fontSize: 12 }}>Loading…</div>
                                        ) : insightsTxnData.length === 0 ? (
                                          <div style={{ color: "#6B7280", fontSize: 12 }}>No matching transactions found.</div>
                                        ) : (
                                          <div style={{ overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                              <thead>
                                                <tr style={{ background: "#DBEAFE" }}>
                                                  {["Date/Time", "Amount", "Direction", "Type", "Counterparty", "Country", "Description"].map((h) => (
                                                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontWeight: 800, color: "#1E3A5F", borderBottom: "1.5px solid #BFDBFE", whiteSpace: "nowrap" }}>{h}</th>
                                                  ))}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {insightsTxnData.map((t, ti) => (
                                                  <tr key={ti} style={{ background: ti % 2 === 0 ? "#EFF6FF" : "#F8FAFC" }}>
                                                    <td style={{ padding: "5px 10px", whiteSpace: "nowrap", color: "#374151" }}>{t.post_date?.replace("T", " ").replace("+00", "").slice(0, 16)}</td>
                                                    <td style={{ padding: "5px 10px", fontWeight: 800, color: "#0B1F33", whiteSpace: "nowrap" }}>${Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td style={{ padding: "5px 10px" }}>
                                                      <span style={{ padding: "2px 7px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: (t.direction === "INBOUND" || t.direction === "CREDIT") ? "#DCFCE7" : "#FEE2E2", color: (t.direction === "INBOUND" || t.direction === "CREDIT") ? "#166534" : "#991B1B" }}>{t.direction}</span>
                                                    </td>
                                                    <td style={{ padding: "5px 10px", color: "#374151", whiteSpace: "nowrap" }}>{t.transaction_code || "—"}</td>
                                                    <td style={{ padding: "5px 10px", color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.counterparty_name}>{t.counterparty_name || "—"}</td>
                                                    <td style={{ padding: "5px 10px", color: "#6B7280" }}>{t.counterparty_country_code || "—"}</td>
                                                    <td style={{ padding: "5px 10px", color: "#6B7280", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.description}>{t.description || "—"}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
                                              {insightsTxnData.length} transaction{insightsTxnData.length !== 1 ? "s" : ""}
                                              {insightsTxnFallback === "all_in_period" ? " · no rule-specific match; showing all in period" : insightsTxnFallback === "extended_window" ? " · no exact match; showing 30-day window" : " matching rule criteria"}
                                            </div>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <PaginationBar
                        page={insightsAcctPage}
                        hasMore={insightsAcctHasMore}
                        loading={loadingInsightsAccts}
                        count={insightsAccounts.length}
                        onPrev={() => setInsightsAcctPage((p) => Math.max(0, p - 1))}
                        onNext={() => setInsightsAcctPage((p) => p + 1)}
                      />
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          </div>
        </>
      )} {/* end Program Insights */}

    </SSBReportShell>
  );
}